import meleElement from 'meleElement';
import { VideoDataStream, CameraHandler, CameraSwitcher, CameraTorchController, STATE } from '@soitron/camera-handler';
import { parseRatio, shallowAssign, timeDelay } from '@soitron/global-helpers';
import { SliderController } from './src/region-of-interest/slider/slider-controller';
import { RegionOfInterest } from './src/region-of-interest/region-of-interest';
import { ControlsController } from './src/controls/controls';
import layout from './src/layout';

/**
 *
 * @param {HTMLElement} el
 */
async function toggleFullScreen(el) {
  if (!document.fullscreenElement) {
    await el.requestFullscreen();
  } else {
    document.exitFullscreen?.();
  }
}

/**
 * @typedef {Object} ROIData
 * Configuration data for the Region of Interest area.
 * @property {number} [x] - The x-coordinate offset.
 * @property {number} [y] - The y-coordinate offset.
 * @property {number} [width] - The width of the ROI.
 * @property {number} [height] - The height of the ROI.
 * @property {number} [scale] - The current zoom/scale factor.
 */

/**
 * @typedef {('environment'|'user')} FacingMode
 * The facing mode of the camera.
 */

/**
 * Configuration for component properties.
 * These properties are automatically bound to HTML attributes.
 */
const PROPERTIES = shallowAssign(
  {},
  {
    debug: {
      type: 'boolean',
      default: false,
      description: '',
    },

    // --- Camera Configuration ---
    camera: {
      type: 'string',
      default: 'environment',
      description: 'Camera device ID or facing mode ("environment" for back, "user" for front).',
    },
    videowidth: {
      type: 'number',
      default: 1920,
      description: 'Desired video resolution width in pixels.',
    },
    videoheight: {
      type: 'number',
      default: 1080,
      description: 'Desired video resolution height in pixels.',
    },
    videoloadtime: {
      type: 'number',
      default: 10000,
      description: 'Timeout in milliseconds for loading the video stream metadata before throwing an error.',
    },
    // Video Image Capture stream
    datastream: {
      type: 'boolean',
      default: true,
      description: 'If true, enables real-time video data stream frame capture and mmFrame messages.',
    },
    processingFrequency: {
      type: 'number',
      default: 15,
      description: 'Frequency (FPS) at which the data stream processes frames.',
    },
    outputSize: {
      type: 'number',
      default: 640,
      description: 'Target size (width) of the ROI image data in pixels.',
    },
    resize: {
      type: 'boolean',
      default: false,
      description: 'If true, forces output to exact resizeWidth x resizeHeight dimensions.',
    },
    resizeWidth: {
      type: 'number',
      default: 640,
      description: 'Target width when resize is enabled.',
    },
    resizeHeight: {
      type: 'number',
      default: 640,
      description: 'Target height when resize is enabled.',
    },
    // --- Feature Flags ---
    fadein: { type: 'boolean', default: true, description: 'If true, applies a CSS fade-in effect when the camera becomes ready.' },
    autoplay: { type: 'boolean', default: true, description: 'If true, the camera stream starts automatically when the component initializes.' },
    // --- Controls Flags ---
    controls: { type: 'boolean', default: true, description: 'If true, renders control panel.' },
    enableSwitchControls: {
      type: 'boolean',
      default: false,
      description: 'If true, enables the logic and UI for switching between front/back cameras.',
    },
    enableTorchControls: { type: 'boolean', default: false, description: 'If true, enables the logic and UI for controlling the camera flashlight.' },

    // --- ROI (Region of Interest) Configuration ---
    roiDraw: { type: 'boolean', default: true, description: 'If true, renders the Region of Interest (ROI) rectangle overlay on video frames.' },

    roiScaleFactorValue: { type: 'number', default: 1.5, description: 'Initial zoom scale factor.' },
    roiScaleFactorMin: { type: 'number', default: 0.5, description: 'Minimum zoom scale factor.' },
    roiScaleFactorMax: { type: 'number', default: 2.2, description: 'Maximum zoom scale factor.' },
    roiScaleFactorStep: { type: 'number', default: 0.025, description: 'Step size for zoom adjustment.' },

    roiRatio: { type: 'string', default: '1/1', description: 'Aspect ratio of the ROI rectangle (e.g., "16/9", "16:9", "4-3").' },
    roiWidth: { type: 'number', default: 300, description: 'Initial width of the ROI rectangle in pixels.' },
    roiHorizontalOffset: { type: 'number', default: 0, description: '.' },
    roiVerticalOffset: { type: 'number', default: 0, description: '' },
    roiCenter: { type: 'boolean', default: true, description: '' },
    roiX: { type: 'number', default: 0, description: '' },
    roiY: { type: 'number', default: 0, description: '' },
    roiBoxSelector: { type: 'string', default: '.box', description: '' },
    roiPadding: { type: 'number', default: 0, description: '' },

    // --- Image Optimization ---
    imageOptimization: { type: ['object', 'boolean'], default: undefined, description: 'Nastavenia obrazu kamery (exposure, brightness, contrast, sharpness). false = vypnuté, undefined = auto defaults per kamera, { front: {...}, back: {...} } = per-camera config.' },

    // --- Event Callbacks ---
    eventStart: { type: 'function', description: 'Callback invoked when camera successfully starts.' },
    eventPlay: { type: 'function', description: 'Callback invoked when video playback begins.' },
    eventStop: { type: 'function', description: 'Callback invoked when camera stream is stopped.' },
    eventPause: { type: 'function', description: 'Callback invoked when video is paused.' },
    eventRestart: { type: 'function', description: 'Callback invoked when video is restarted.' },
    eventCameraToggle: { type: 'function', description: 'Callback invoked when camera device is switched.' },
    eventCameraLight: { type: 'function', description: 'Callback invoked when camera torch state changes.' },
    eventChangeControlsPosition: { type: 'function', description: '' },
  },
  meleElement._properties
);

/**
 * A Web Component that manages camera video streaming, Region of Interest (ROI) selection,
 * and real-time frame data processing.
 *
 * @element mele-camera
 * @extends {meleElement}
 *
 * @example
 * // HTML Usage
 * <mele-camera
 *   camera="environment"
 *   videowidth="1920"
 *   autoplay="true"
 *   roi-draw="true"
 * ></mele-camera>
 *
 * @example
 * // JS Usage
 * const cam = document.querySelector('mele-camera');
 * await cam.cameraInit();
 * cam.onDataStream((frameData) => {
 *   console.log('Frame processed:', frameData);
 * });
 */
export default class MeleCamera extends meleElement {
  /**
   * Static property definitions for meleElement to bind attributes.
   * @type {Object}
   */
  static _properties = PROPERTIES;

  /**
   * List of attributes to observe for changes.
   * @type {string[]}
   */
  static observedAttributes = Object.keys(PROPERTIES);

  _videoElement = null;
  /** @type {CameraHandler|null} */
  _cameraHandler = null;
  /** @type {VideoDataStream|null} */
  _cameraDataStream = null;
  /**
   * @type {string|null}
   * @private
   * @description DEBUG-only: podpis poslednej zalogovanej geometrie ROI overlay-u
   * (`_updateDebugCaptureRect`). Slúži len na to, aby sa do konzoly logovalo iba
   * pri reálnej zmene (rozlíšenie videa / veľkosť elementu / výrez), nie každý frame.
   */
  _debugCaptureRectSignature = null;
  /** @type {CameraSwitcher|null} */
  _cameraSwitcher = null;
  /** @type {CameraTorchController|null} */
  _cameraTorch = null;
  /** @type {ControlsController|null} */
  _controls = null;
  /** @type {RegionOfInterest|null} */
  _roi = null;
  /** @type {SliderController|null} */
  _slider = null;

  /**
   * @type {number}
   * @readonly
   * @private
   * @description
   * Medzera (v px) medzi vnutornou hranou ovladacieho panela a zoom posuvnikom
   * pri jeho dynamickom umiestneni. Pouziva ju `_positionSliderNextToPanel()`:
   * offset posuvnika = sirka_panela + tato medzera. Zabranuje tomu, aby sa
   * posuvnik dotykal panela. Hodnota zodpoveda vnutornemu rozostupu tlacidiel
   * v paneli (`gap: 15px`), aby posobil opticky konzistentne.
   */
  SLIDER_PANEL_GAP_PX = 15;

  _cameraOnCancel = null;
  _cameraOnChangePosition = null;

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  /**
   * Lifecycle hook called when the element is initialized.
   * Sets up the DOM layout and references.
   * @override
   * @returns {Promise<void>}
   */
  async init() {
    this.appendChildren(layout);
    this.classList.add('camera-video-element');
    this._videoElement = this.find('video');
    super.init();
  }

  /**
   * Main initialization method to start the camera logic.
   * Can be called manually if the component is not set to autoplay,
   * or if specific initialization timing is required.
   *
   * @public
   * @returns {Promise<MeleCamera>} Returns the component instance for chaining.
   */
  async cameraInit() {
    // 1. UI Setup
    if (!this.fadein) this.classList.add('show');
    if (this.controls) this._setupControls();

    // 2. Core Handler Setup
    this._setupCameraHandler();

    // await timeDelay(5000);
    setTimeout(() => {
      // 3. Feature Setup
      // if (this.enableSwitchControls)
      this._setupSwitcher();
      // if (this.enableTorchControls)
      this._setupTorch();
      // if (this.datastream)
      this._setupDataStream();
    }, 2000);

    //this.find('.fullscreen').addEventListener('click', () => toggleFullScreen(this));

    return this;
  }

  // ===========================================================================
  // PUBLIC API (Playback & Control)
  // ===========================================================================

  /**
   * Starts the camera stream.
   * Triggers the `eventStart` callback.
   *
   * @public
   * @param {boolean} [autoplay] - If true, immediately calls `play()` after stream start.
   * @returns {Promise<void>}
   */
  async start(autoplay) {
    this.eventStart?.(autoplay);
    await this._cameraHandler?.__startStream();
    if (autoplay) {
      await this.play();
    }
  }

  /**
   * Plays the video playback and starts data streaming.
   * Applies the current ROI configuration and triggers `eventPlay`.
   *
   * `pause()` zastaví iba `<video>` element, `VideoDataStream` NEZASTAVUJE - jeho
   * `requestVideoFrameCallback` registrácia ostáva pending (pauznuté video len
   * neprezentuje nové framy, takže sa callback nevyvolá). Keby sa `__start()` zavolalo
   * bez predchádzajúceho `__stop()`, prepísalo by iba `_requestVideoFrameCallbackId`
   * novou hodnotou a stará pending registrácia by po obnovení prehrávania rozbehla
   * DRUHÝ paralelný capture reťazec. Každé ďalšie play/pause by pridalo ďalší, čím by
   * sa násobil počet spracovaných framov a prestal by fungovať decimačný filter cez
   * `processingFrequency`. Explicitné `__stop()` pred `__start()` drží vždy práve
   * jeden aktívny reťazec.
   *
   * @public
   * @returns {Promise<void>}
   */
  async play() {
    await this._cameraHandler?.__playVideoPlayback();
    this._cameraDataStream?.__stop();
    this._cameraDataStream?.__start();

    // Centrovať ROI do viditeľnej oblasti (odpočítať panel)
    if (this._roi) {
      const panelWidth = this._getControlsPanelWidth();
      const isLeft = this._controls?._wrapper?.classList?.contains('position-left');
      const offset = panelWidth > 0 ? (isLeft ? panelWidth / 2 : -(panelWidth / 2)) : 0;
      this._roi.__applyConfiguration({ horizontalOffset: offset });
    }

    if (this.fadein) this.classList.add('show');
    this.eventPlay?.();
  }

  /**
   * Stops the camera stream and data processing.
   * Removes the visual class and triggers `eventStop`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async stop() {
    if (this.fadein) this.classList.remove('show');

    this._removeSoftLight()
    this._cameraDataStream?.__stop();
    await this._cameraHandler?.__stopStream();
    this.eventStop?.();
  }

  /**
   * Pauses the video playback.
   * Triggers `eventPause`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async pause() {
    await this._cameraHandler?.__pauseVideoPlayback();
    this.eventPause?.();
  }

  /**
   * Restarts the camera stream and playback.
   * Useful when stream is interrupted or device configuration changes.
   * Triggers `eventRestart`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async restart() {
    await this._cameraHandler?.__restartStreamAndPlay();
    this.eventRestart?.();
  }

  // ===========================================================================
  // FEATURES (Switch & Light)
  // ===========================================================================

  /**
   * Toggles between available camera devices (e.g., front/back).
   * Does nothing if switching is disabled or unavailable.
   * Triggers `eventCameraToggle` with the new facing mode.
   *
   * @public
   * @returns {Promise<void>}
   */
  async cameraToggle() {
    if (!this._cameraSwitcher || !this.canToggle) return;

    // Odstrániť prípadný facingMode constraint pred prepnutím - inak kolíduje
    // s deviceId: {exact: ...} a getUserMedia hodí OverconstrainedError
    // (napr. pri prepnutí na prednú kameru keď config má facingMode: {exact: 'environment'}).
    const cfg = this._cameraHandler?.__cameraVideoConfig;
    if (cfg) delete cfg.facingMode;

    await this._cameraSwitcher.__toggleCameraDevice();
    this._updateLightButton();
    this.eventCameraToggle?.(this._cameraSwitcher.__getCurrentCameraFacingMode);
  }

  /**
   * Prepne svetlo kamery.
   * Ak je dostupný HW torch (zadná kamera na mobile) - použije ho.
   * Ak je predná kamera (user) - zobrazí biely overlay (soft light) ktorý osvetlí doklad.
   * Ak je zadná kamera bez torch (Getac tablet) - tlačidlo je disabled, nič nerobí.
   *
   * @public
   * @param {boolean} [turnOnState] - Vynúti konkrétny stav (true=on, false=off). Ak undefined, togglene.
   * @returns {Promise<void>}
   */
  async cameraLight(turnOnState) {
    // HW torch dostupný - použiť ho (mobil, zadná kamera)
    if (this._cameraTorch && this.hasLight) {
      await this._cameraTorch.__toggleTorch(turnOnState)
      this.eventCameraLight?.(this._cameraTorch.__getIsCurrentlyOn)
      return
    }

    // Predná kamera - soft light (biely overlay)
    if (this._isFrontCamera()) {
      const newState = turnOnState !== undefined ? !!turnOnState : !this._softLightOn
      this._toggleSoftLight(newState)
      this.eventCameraLight?.(this._softLightOn)
      return
    }

    // Zadná kamera bez torch - nič (tlačidlo je disabled)
  }

  // ===========================================================================
  // GETTERS & STATE
  // ===========================================================================

  /**
   * Returns the frozen object of possible camera states.
   * @readonly
   * @returns {Object} Enum of camera states.
   */
  get stateList() {
    return Object.freeze(STATE);
  }

  /**
   * Returns the current internal state of the camera handler.
   * @readonly
   * @returns {string|number} The current state.
   */
  get state() {
    return this._cameraHandler?.__getCurrentState;
  }

  /**
   * Checks if camera switching is available on this device.
   * @readonly
   * @returns {boolean}
   */
  get canToggle() {
    return !!this._cameraHandler?.__canToggleCamera;
  }

  /**
   * Checks if the camera torch (flashlight) is supported and available.
   * @readonly
   * @returns {boolean}
   */
  get hasLight() {
    return !!this._cameraTorch?.__checkIsSupported;
  }

  // ===========================================================================
  // EVENT BINDINGS
  // ===========================================================================

  /**
   * Registers a callback for the UI cancel action.
   * @public
   * @param {Function} callback - Function to execute when cancel is clicked.
   */
  onCancel(callback) {
    if (!callback) return;
    this._cameraOnCancel = callback;
    this._controls?.__actionCancel(() => this._cameraOnCancel?.());
  }

  /**
   * Registers a callback for the ROI position change action.
   * Also binds the UI logic to toggle slider position.
   * @public
   * @param {Function} callback - Function to execute when position button is clicked.
   */
  onChangePosition(callback) {
    if (!callback) return;
    this._cameraOnChangePosition = callback;

    this._controls?.__actionPosition(() => {
      this._slider?.__togglePositionLeft();
      this._controls?.__positionToggle();

      // Vypočítať offset podľa šírky panela - ROI sa centruje do viditeľnej oblasti
      const panelWidth = this._getControlsPanelWidth();
      const isLeft = this._controls?._wrapper?.classList?.contains('position-left');
      const offset = isLeft ? panelWidth / 2 : -(panelWidth / 2);

      this._roi?.__applyConfiguration({ horizontalOffset: offset });

      // Aktualizovať soft-light ak je zapnutý
      if (this._softLightOn) {
        setTimeout(() => this._updateSoftLightPosition(), 200);
      }

      this._cameraOnChangePosition?.();
    });
  }

  /**
   * Vráti šírku ovládacieho panela kamery (pre výpočet centrovania ROI).
   * @private
   * @returns {number}
   */
  _getControlsPanelWidth() {
    const panel = this.find('.camera-controls-container');
    if (!panel || panel.style.display === 'none') return 0;
    return panel.getBoundingClientRect().width || 0;
  }

  /**
   * Registers a callback invoked when the camera handler is fully ready.
   * @public
   * @param {Function} callback
   */
  onReady(callback) {
    this._cameraHandler?.__onReady(callback);
  }

  /**
   * Registers a listener for the video data stream.
   * This function receives the processed frame data.
   * @public
   * @param {TDataStreamCallback} callback
   */
  onDataStream(callback) {
    /**
     * @type {HTMLCanvasElement}
     */
    let canvas;
    /**
     * @type {CanvasRenderingContext2D}
     */
    let ctx;
    if (this.debug) {
      canvas = this.find('.box > canvas.debug-roi-canvas');
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'debug-roi-canvas';
        canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;filter:grayscale(1);pointer-events:none;z-index:1;';
        this.find('.box').prepend(canvas);
      }
      ctx = canvas.getContext('2d');
    }

    this._cameraDataStream?.__onDataStream((id, streamData, metadata) => {
      callback?.(id, streamData, metadata);

      if (this.debug && canvas && ctx) {
        // drawImage so the image scales to fill the entire ROI box
        createImageBitmap(streamData).then(bitmap => {
          const displayW = canvas.clientWidth;
          const displayH = canvas.clientHeight;
          if (canvas.width !== displayW || canvas.height !== displayH) {
            canvas.width = displayW;
            canvas.height = displayH;
          }
          ctx.drawImage(bitmap, 0, 0, displayW, displayH);
          bitmap.close();
        });

        // DEBUG overlay: prekresliť, kde REÁLNE v zobrazenom videu leží
        // zachytený výrez (porovnanie s vizuálnym `.roi-guide` rámčekom).
        this._updateDebugCaptureRect();
      }
    });
  }

  /**
   * DEBUG-ONLY diagnostika posunu ROI vs. reálne skenovaného výrezu.
   *
   * Prečo existuje: vizuálny rámček (`.roi-guide`) je iba CSS prvok nakreslený
   * v súradniciach kontajnera. Reálne sa ale dekodéru posiela výrez vypočítaný
   * v `VideoDataStream._computeROI()` v NATÍVNYCH pixeloch videa, pričom video
   * je vykreslené s `object-fit: cover`. Ak sa prepočet CSS->natívne mýli (napr.
   * nezohľadní `cover` orezanie/centrovanie alebo použije nerovnaké mierky pre os
   * x a y), reálne skenovaná oblasť sa NEKRYJE s nakresleným rámčekom a používateľ
   * musí doklad/QR posunúť mimo zóny, aby sa naskenoval.
   *
   * Táto metóda vezme reálny výrez (`this._cameraDataStream.__getComputedROI`,
   * natívne px) a SPRÁVNYM `cover` prepočtom ho zmapuje späť na zobrazené (CSS)
   * súradnice videa. Výsledok vykreslí ako červený obdĺžnik priamo nad živý obraz.
   * Vizuálne porovnanie: zelený `.roi-guide` = zamýšľaná zóna, červený rám =
   * oblasť, ktorej pixely reálne idú do dekodéra. Posun medzi nimi = chyba.
   *
   * `cover` mapovanie (natívne px -> zobrazené CSS px):
   *   coverScale = max(displayW / videoW, displayH / videoH)   // jednotná mierka
   *   offsetX    = (videoW * coverScale - displayW) / 2         // orezané po stranách
   *   displayX   = nativeX * coverScale - offsetX
   * (analogicky pre os y)
   *
   * Volá sa raz za spracovaný frame z `onDataStream` a iba ak je `this.debug`
   * true. Nemá žiadny vedľajší efekt na skenovanie - je čisto vizuálna pomôcka.
   * Pri zmene geometrie (rozlíšenie videa, veľkosť elementu, výrez) jednorazovo
   * zaloguje surové čísla do konzoly cez `console.log2`, aby bolo možné posun
   * potvrdiť aj kvantitatívne, nielen okom.
   *
   * @private
   * @returns {void}
   */
  _updateDebugCaptureRect() {
    const video = this._videoElement;
    const stream = this._cameraDataStream;
    if (!video || !stream) return;

    const videoW = video.videoWidth;
    const videoH = video.videoHeight;
    const displayW = video.clientWidth;
    const displayH = video.clientHeight;
    // Bez platnej geometrie (kamera ešte nedoručila frame, element 0x0) nemá
    // zmysel počítať - prepočet by delil nulou alebo vrátil NaN.
    if (!videoW || !videoH || !displayW || !displayH) return;

    // `object-fit: cover` -> obraz sa zväčší jednou mierkou tak, aby pokryl celý
    // element, a prebytok sa symetricky oreže (vycentruje).
    const coverScale = Math.max(displayW / videoW, displayH / videoH);
    const offsetX = (videoW * coverScale - displayW) / 2;
    const offsetY = (videoH * coverScale - displayH) / 2;

    // Reálny výrez v natívnych px videa (to, čo dostane dekodér).
    const roi = stream.__getComputedROI;

    // Mapovanie späť na zobrazené CSS súradnice videa.
    const rectY = roi.y * coverScale - offsetY;
    const rectW = roi.width * coverScale;
    const rectH = roi.height * coverScale;
    let rectX = roi.x * coverScale - offsetX;
    // Mirror-aware: ak je video CSS-zrkadlené (`data-facing-mode="user"`), natívny
    // výrez sa na obrazovke zobrazuje preklopený. Aby červený rám sadol na to, čo
    // je reálne vidno (a teda na biely box), preklopíme aj jeho x.
    const mirrored = this._videoElement?.dataset?.facingMode === 'user';
    if (mirrored) rectX = displayW - (rectX + rectW);

    // Overlay sa vkladá do ROI kontajnera, ktorý zdieľa počiatok aj veľkosť so
    // zobrazeným videom (oba sú 100% hostiteľského elementu), takže červený rám
    // sedí 1:1 s tým, čo je vidno na obraze.
    const container = this.find('.region-of-interest-container');
    if (!container) return;

    let rectEl = container.querySelector('.debug-capture-rect');
    if (!rectEl) {
      rectEl = document.createElement('div');
      rectEl.className = 'debug-capture-rect';
      // Inline štýl zámerne - je to dočasná diagnostická pomôcka mimo SCSS.
      rectEl.style.cssText =
        'position:absolute;border:2px solid red;box-sizing:border-box;pointer-events:none;z-index:3;';
      container.appendChild(rectEl);
    }

    rectEl.style.left = rectX + 'px';
    rectEl.style.top = rectY + 'px';
    rectEl.style.width = rectW + 'px';
    rectEl.style.height = rectH + 'px';

    // Jednorazový log pri zmene geometrie - kvantitatívne potvrdenie posunu.
    const signature = `${videoW}x${videoH}|${displayW}x${displayH}|${roi.x},${roi.y},${roi.width},${roi.height}`;
    if (this._debugCaptureRectSignature !== signature) {
      this._debugCaptureRectSignature = signature;
      console.log2(
        `[ROI-DEBUG] video=${videoW}x${videoH} display=${displayW}x${displayH} ` +
        `coverScale=${coverScale.toFixed(3)} offset=${offsetX.toFixed(1)},${offsetY.toFixed(1)} ` +
        `nativeROI=${roi.x},${roi.y},${roi.width},${roi.height} ` +
        `screenRect=${rectX.toFixed(1)},${rectY.toFixed(1)},${rectW.toFixed(1)},${rectH.toFixed(1)}`
      );
    }
  }

  /**
   * Removes the data stream listener.
   * @public
   */
  removeOnDataStream() {
    this._cameraDataStream?.__removeOnDataStream();
  }

  /**
   * Updates VideoDataStream configuration at runtime (e.g., resize, resizeWidth, resizeHeight).
   * @public
   * @param {Partial<{resize: boolean, resizeWidth: number, resizeHeight: number, minOutputSize: number, processingFrequency: number}>} config
   */
  updateDataStreamConfig(config) {
    this._cameraDataStream?.__updateConfig(config);
  }

  /**
   * Updates ROI configuration at runtime (e.g., ratio, scale, width).
   * @public
   * @param {Object} config - ROI config (ratio, scale, width, center, horizontalOffset, verticalOffset)
   */
  updateROIConfig(config) {
    this._roi?.__applyConfiguration(config);
  }

  // ===========================================================================
  // IMAGE OPTIMIZATION API
  // ===========================================================================

  /**
   * Vráti capabilities aktuálneho video tracku - zistenie čo kamera reálne podporuje.
   * Chrome na Windows používa IAMCameraControl, výsledok sa líši medzi platformami.
   * @public
   * @returns {MediaTrackCapabilities|null}
   */
  getTrackCapabilities() {
    return this._cameraHandler?.__getTrackCapabilities || null;
  }

  /**
   * Vráti aktuálne nastavenia video tracku (reálne hodnoty).
   * @public
   * @returns {MediaTrackSettings|null}
   */
  getTrackSettings() {
    return this._cameraHandler?.__getTrackSettings || null;
  }

  /**
   * Aplikuje image constraints na aktívny track (expozícia, WB, focus, ...).
   * @public
   * @param {Object} constraints - Image parametre (exposureMode, exposureTime, whiteBalanceMode, ...)
   * @returns {Promise<boolean>}
   */
  async applyImageConstraints(constraints) {
    return this._cameraHandler?.__applyImageConstraints(constraints) || false;
  }

  // ===========================================================================
  // PRIVATE HELPERS (Initialization Logic)
  // ===========================================================================

  /**
   * Initializes the overlay controls (buttons).
   * @private
   */
  _setupControls() {
    this._controls = new ControlsController(this.find('.camera-controls-container'));
    this._controls.__show();
  }

  /**
   * Initializes the core CameraHandler logic.
   * Creates a video element if one does not exist in the layout.
   * @private
   */
  _setupCameraHandler() {
    this._cameraHandler = new CameraHandler(this._videoElement, {
      __cameraVideoConfig: {
        facingMode: { exact: this.camera },
        width: { ideal: this.videowidth },
        height: { ideal: this.videoheight },
      },
      __global: { __videoLoadTime: this.videoloadtime },
      __imageOptimization: this.imageOptimization,
    });

    // Fallback if video element wasn't found in DOM
    if (!this._videoElement) {
      const generatedVideo = this._cameraHandler.__getVideoElement;
      generatedVideo.classList.add('camera-video', 'camera-video-autogenerated');
      this.prepend(generatedVideo);
    }
  }

  /**
   * Initializes the camera switcher logic if enabled.
   * @private
   */
  _setupSwitcher() {
    this._cameraSwitcher = new CameraSwitcher(this._cameraHandler);
    // Bindovať vždy - canToggle môže byť false v čase _setupSwitcher (async enumerateDevices
    // ešte nedobehol), ale stať sa true neskôr. cameraToggle() robí early return sám.
    this._controls?.__actionSwitch(() => this.cameraToggle());
  }

  /**
   * Inicializuje torch controller a tlačidlo svetla.
   * Tlačidlo sa zobrazuje vždy - správanie sa líši podľa typu kamery:
   * - HW torch dostupný (mobil, zadná kamera) -> toggle HW torch
   * - Predná kamera -> soft light (biely overlay)
   * - Zadná kamera bez torch (Getac) -> tlačidlo disabled
   * @private
   */
  _setupTorch() {
    this._cameraTorch = new CameraTorchController(this._cameraHandler)
    this._softLightOn = false
    this._softLightEl = null

    // Vždy zobraziť tlačidlo svetla
    this._controls?.__actionLight(() => this.cameraLight())

    // Ak zadná kamera bez HW torch - disabled stav
    if (!this.hasLight && !this._isFrontCamera()) {
      const btn = this.find('.camera-control-button-light')
      if (btn) btn.classList.add('disabled')
    }
  }

  /**
   * Aktualizuje stav tlačidla svetla podľa aktuálnej kamery.
   * Volané po prepnutí kamery (cameraToggle).
   * @private
   */
  _updateLightButton() {
    const btn = this.find('.camera-control-button-light')
    if (!btn) return

    // Vypnúť soft light ak bol zapnutý
    this._removeSoftLight()

    if (this.hasLight || this._isFrontCamera()) {
      btn.classList.remove('disabled')
    } else {
      btn.classList.add('disabled')
    }
  }

  /**
   * Zistí či je aktuálne aktívna predná kamera (facing mode 'user').
   * @private
   * @returns {boolean}
   */
  _isFrontCamera() {
    const facing = this._cameraSwitcher?.__getCurrentCameraFacingMode
    if (facing) return facing === 'user'
    // Fallback na config hodnotu
    return this.camera === 'user'
  }

  /**
   * Prepne soft light overlay (biely rám okolo ROI ktorý osvetlí doklad pred prednou kamerou).
   * Element sa zarovnáva s ROI boxom - biely box-shadow vytvára osvetlenie okolo.
   * @private
   * @param {boolean} on - Zapnúť/vypnúť
   */
  _toggleSoftLight(on) {
    this._softLightOn = on

    if (on) {
      if (!this._softLightEl) {
        this._softLightEl = document.createElement('div')
        this._softLightEl.className = 'camera-soft-light'
        this.prepend(this._softLightEl)
      }

      this._updateSoftLightPosition()

      // Force reflow pred pridaním triedy (fade-in)
      this._softLightEl.offsetHeight
      this._softLightEl.classList.add('active')
    } else if (this._softLightEl) {
      this._softLightEl.classList.remove('active')
    }
  }

  /**
   * Aktualizuje pozíciu soft-light overlay podľa aktuálnej pozície guide rámčeku.
   * @private
   */
  _updateSoftLightPosition() {
    if (!this._softLightEl) return
    const guide = this.find('.region-of-interest-container .roi-guide')
    if (guide) {
      const rect = guide.getBoundingClientRect()
      const parentRect = this.getBoundingClientRect()
      this._softLightEl.style.left = (rect.left - parentRect.left) + 'px'
      this._softLightEl.style.top = (rect.top - parentRect.top) + 'px'
      this._softLightEl.style.width = rect.width + 'px'
      this._softLightEl.style.height = rect.height + 'px'
      this._softLightEl.style.borderRadius = '8px'
    }
  }

  /**
   * Odstráni soft light overlay z DOM.
   * @private
   */
  _removeSoftLight() {
    if (this._softLightEl) {
      this._softLightEl.remove()
      this._softLightEl = null
    }
    this._softLightOn = false
  }

  /**
   * Initializes the VideoDataStream and ROI logic.
   * @private
   */
  _setupDataStream() {
    this._cameraDataStream = new VideoDataStream(this._cameraHandler, {
      minOutputSize: this.outputSize,
      processingFrequency: this.processingFrequency,
      resize: this.resize,
      resizeWidth: this.resizeWidth,
      resizeHeight: this.resizeHeight,
    });

    if (this.roiDraw) {
      this._setupROI();
    }
  }

  /**
   * Initializes the Region of Interest (ROI) visual elements.
   * @private
   */
  _setupROI() {
    const roiContainer = this.find('.region-of-interest-container');
    if (!roiContainer) return;

    this._roi = new RegionOfInterest(roiContainer, {
      width: this.roiWidth,
      ratio: parseRatio(this.roiRatio),
      scale: this.roiScaleFactorValue,
      horizontalOffset: this.roiHorizontalOffset,
      verticalOffset: this.roiVerticalOffset,
      center: this.roiCenter,
      x: this.roiX,
      y: this.roiY,
      boxSelector: this.roiBoxSelector,
      padding: this.roiPadding,
    });

    // Sync ROI resize events with data stream config + soft-light
    this._roi.__registerResizeHandler(data => {
      this._cameraDataStream?.__updateConfig(data);
      // Aktualizovať soft-light pozíciu ak je zapnutý
      if (this._softLightOn) this._updateSoftLightPosition();
    });

    if (this.controls) this._setupSlider();
  }

  /**
   * Initializes the Zoom slider control.
   * @private
   */
  _setupSlider() {
    this._slider = new SliderController(this.find('.slider-wrapper'));

    this._slider.__onValueChange(value => {
      this._roi?.__applyConfiguration({ scale: value });
    });

    this._slider.__min = this.roiScaleFactorMin;
    this._slider.__max = this.roiScaleFactorMax;
    this._slider.__step = this.roiScaleFactorStep;
    this._slider.__value = this.roiScaleFactorValue;

    this._controls?.__actionZoom(() => {
      // Pred KAZDYM zobrazenim posuvnik dynamicky umiestnit vedla panela.
      // Sirka panela sa meni podla dlzky textu na tlacidlach (jazyk, pocet
      // viditelnych tlacidiel), preto sa pozicia pocita az tesne pred zobrazenim,
      // nie raz pri inicializacii. Pri skryvani prepocet netreba.
      if (!this._slider?.__isActive) this._positionSliderNextToPanel();
      this._slider?.__toggleVisibility();
    });
  }

  /**
   * Umiestni zoom posuvnik tesne vedla ovladacieho panela kamery.
   *
   * Posuvnik aj panel su absolutne pozicovane voci tomu istemu okraju obrazovky
   * (default vpravo, po prepnuti pozicie vlavo). Panel je ukotveny na kraji
   * (`right: 0`) a jeho sirka rastie s dlzkou textu na tlacidlach. Posuvnik ma
   * v SCSS staticky offset `--slider-position: 150px`; ak je panel sirsi nez tento
   * offset, panel posuvnik prekryje. Tato metoda nacita aktualnu sirku panela a
   * nastavi offset posuvnika na `sirka_panela + SLIDER_PANEL_GAP_PX`, cim sa
   * posuvnik zarovna tesne k hrane panela bez prekrytia.
   *
   * Ak sa sirku panela nepodari zmerat (panel skryty, este nevykresleny), offset
   * sa nemeni a plati posledna platna (resp. SCSS) hodnota - bezpecny fallback.
   *
   * @private
   * @see _getControlsPanelWidth
   * @see SliderController#__setPositionOffset
   */
  _positionSliderNextToPanel() {
    if (!this._slider) return;
    const panelWidth = this._getControlsPanelWidth();
    if (panelWidth <= 0) return;
    this._slider.__setPositionOffset(panelWidth + this.SLIDER_PANEL_GAP_PX);
  }
}

window.customElements.define('mele-camera', MeleCamera);
